// The payroll register, and a person's own statement.
//
// Both render `payable`, which is NOT take-home pay — gross less the attendance
// shortfall deduction, plus any prior-period adjustment, and nothing else. The
// caption saying so is not decoration: PF, ESI and TDS are out of scope, and a
// figure labelled "net" would be one careless read away from a wrong payment.
import { useCallback, useEffect, useState } from 'react';
import { Icon, Badge, Button, Mono, Select } from './primitives';
import { PCard, SensitiveMark, fmtINRFull } from './people';
import { PeriodStepper } from './card';
import { InlineStatus, LoadError, useToast } from './feedback';
import { downloadCsv } from '../lib/csv';
import { monthRange, orgToday } from '../lib/dates';
import {
  loadRegister,
  loadComponents,
  myStatements,
  runPeriod,
  settleAmendment,
  markPaid,
  setShortfallChoice,
  shortfallLeaveTypes,
  PRE_STATUTORY_NOTE,
  type RegisterRow,
  type PayrollLine,
  type Component,
} from '../lib/payroll';
import { listPeriods, monthLabel, type Period, type PeriodStatus } from '../lib/payrollPeriods';

// The same header as the monthly summary stacked under it: ink-3 at medium
// weight. The two tables used two styles a card apart (HRA-18).
const th: React.CSSProperties = {
  textAlign: 'right',
  padding: 'var(--hf-space-2) var(--hf-space-3)',
  fontSize: 'var(--hf-text-2xs)',
  fontWeight: 'var(--hf-weight-medium)',
  textTransform: 'uppercase',
  letterSpacing: 'var(--hf-track-eyebrow)',
  color: 'var(--hf-ink-3)',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid var(--hf-rule)',
};
const td: React.CSSProperties = {
  textAlign: 'right',
  padding: 'var(--hf-space-2) var(--hf-space-3)',
  fontSize: 'var(--hf-text-sm)',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid var(--hf-rule)',
};

const blank = <span aria-hidden="true">·</span>;

// One array driving both the table and the file, so the two can never disagree
// about what a column means. Same shape as MonthlySummary's.
//
// Every money column is full rupees (fmtINRFull): Gross, Deduction and Adjustment
// used the compact "₹1.25 L" beside an exact Payable, so gross − deduction +
// adjustment never visibly equalled payable, and a negative adjustment printed
// "₹-5000" (HRA-11).
type Col = {
  key: string;
  label: string;
  csv: (r: RegisterRow) => string | number;
  cell?: (r: RegisterRow) => React.ReactNode;
  wide?: boolean;
};
const COLUMNS: Col[] = [
  { key: 'name', label: 'Person', csv: (r) => r.full_name ?? '—', wide: true },
  { key: 'dept', label: 'Department', csv: (r) => r.department ?? '—', wide: true },
  { key: 'required', label: 'Required h', csv: (r) => r.required_hours },
  { key: 'worked', label: 'Worked h', csv: (r) => r.worked_hours },
  {
    key: 'comp',
    label: 'Credited h',
    csv: (r) => r.compensated_hours,
    cell: (r) =>
      Number(r.compensated_hours) ? (
        <Mono style={{ color: 'var(--hf-sky-text)' }}>{r.compensated_hours}</Mono>
      ) : (
        blank
      ),
  },
  { key: 'effective', label: 'Effective h', csv: (r) => r.effective_hours },
  {
    key: 'shortfall',
    label: 'Shortfall h',
    csv: (r) => r.shortfall_hours,
    cell: (r) =>
      Number(r.shortfall_hours) ? (
        // Waived under the threshold reads as ink, not amber: nothing is owed.
        <Mono style={{ color: r.shortfall_waived ? 'var(--hf-ink-3)' : 'var(--hf-amber-text)' }}>
          {r.shortfall_hours}
          {r.shortfall_waived ? ' · waived' : ''}
        </Mono>
      ) : (
        blank
      ),
  },
  {
    // 20261009110000: HR picks pay or leave for a shortfall at or above the
    // waiver. The register renders a picker here on an open month; this is
    // what the file and a locked month show.
    key: 'settle',
    label: 'Shortfall settled by',
    csv: (r) =>
      r.shortfall_waived
        ? 'waived'
        : Number(r.shortfall_leave_days)
          ? `${r.shortfall_leave_days} d ${r.shortfall_leave_type ?? ''} leave`.trim()
          : r.deduction_amount
            ? 'pay'
            : '',
    cell: (r) =>
      Number(r.shortfall_leave_days) ? (
        <Mono style={{ color: 'var(--hf-sky-text)' }}>
          {r.shortfall_leave_days} d leave{r.deduction_amount ? ' + pay' : ''}
        </Mono>
      ) : r.deduction_amount ? (
        <Mono>pay</Mono>
      ) : (
        blank
      ),
  },
  {
    // Approved unpaid leave, beside the shortfall — deliberately NOT labelled
    // "of which". It is not always a subset: hours worked elsewhere in the month
    // can absorb it, which 20260916100000's own header says. It is not deducted
    // separately either; the deduction is identical with or without it.
    key: 'unpaidLeave',
    label: 'Approved unpaid leave h',
    csv: (r) => r.unpaid_leave_hours ?? '',
    cell: (r) =>
      // null is "priced before the column existed", not "none". A confident 0
      // here would claim a month was an unexplained absence.
      r.unpaid_leave_hours == null ? (
        <Mono style={{ color: 'var(--hf-ink-3)' }}>—</Mono>
      ) : Number(r.unpaid_leave_hours) ? (
        <Mono style={{ color: 'var(--hf-amber-text)' }}>{r.unpaid_leave_hours}</Mono>
      ) : (
        blank
      ),
  },
  {
    // A month's overtime is credited as comp off when payroll locks
    // (20261008110000). Before that both are null: "not yet", shown as a dash.
    key: 'overtime',
    label: 'Overtime h',
    csv: (r) => r.overtime_hours ?? '',
    cell: (r) =>
      r.overtime_hours == null ? (
        <Mono style={{ color: 'var(--hf-ink-3)' }}>—</Mono>
      ) : Number(r.overtime_hours) ? (
        <Mono>{r.overtime_hours}</Mono>
      ) : (
        blank
      ),
  },
  {
    key: 'overtimeLeave',
    label: 'Comp off d',
    csv: (r) => r.overtime_leave ?? '',
    cell: (r) =>
      r.overtime_leave == null ? (
        <span title="Credited when this month is locked">
          <Mono style={{ color: 'var(--hf-ink-3)' }}>—</Mono>
        </span>
      ) : Number(r.overtime_leave) ? (
        <Mono style={{ color: 'var(--hf-sky-text)' }}>+{r.overtime_leave}</Mono>
      ) : (
        blank
      ),
  },
  {
    key: 'gross',
    label: 'Gross',
    csv: (r) => r.gross,
    cell: (r) => <Mono>{fmtINRFull(r.gross)}</Mono>,
  },
  {
    key: 'deduction',
    label: 'Deduction',
    csv: (r) => r.deduction_amount,
    cell: (r) =>
      r.deduction_amount ? (
        <Mono style={{ color: 'var(--hf-amber-text)' }}>{fmtINRFull(-r.deduction_amount)}</Mono>
      ) : (
        blank
      ),
  },
  {
    key: 'adjustment',
    label: 'Adjustment',
    csv: (r) => r.adjustment_amount,
    cell: (r) => (r.adjustment_amount ? <Mono>{fmtINRFull(r.adjustment_amount)}</Mono> : blank),
  },
  {
    key: 'payable',
    label: 'Payable (pre-statutory)',
    csv: (r) => r.payable,
    cell: (r) => <Mono style={{ color: 'var(--hf-ink)' }}>{fmtINRFull(r.payable)}</Mono>,
  },
  {
    key: 'autoClosed',
    label: '⚠ auto-closed',
    csv: (r) => r.auto_closed_days,
    cell: (r) => (r.auto_closed_days ? <Badge tone="danger">{r.auto_closed_days}</Badge> : blank),
  },
];

/** The month's status, in the periods card's terms: a locked month is finished,
 *  not waiting, so it is neutral with a lock rather than queue-amber (HRA-20). */
const PeriodBadge = ({ status }: { status: PeriodStatus }) => (
  <Badge tone={status === 'paid' ? 'success' : 'neutral'} dot={status === 'paid'}>
    {(status === 'locked' || status === 'amended') && <Icon name="lock" size={10} />}
    {status}
  </Badge>
);

export function PayrollRegister({
  canRun,
  tz,
  monthActions,
  offset,
  onOffset,
}: {
  canRun: boolean;
  tz: string;
  /** The month's own Lock / Amend / workspace actions, rendered in this card
   *  (they used to be a second card of six months beside it). */
  monthActions?: (year: number, month: number) => React.ReactNode;
  /**
   * Which month, as an offset from the current org month (-1 = last month, the
   * one you would actually run). Owned by the Payroll view, which steps it for
   * this register and the monthly summary under it together: each kept its own
   * month, so August's register sat above September's hours (HRA-12).
   */
  offset: number;
  /**
   * Steps the view's month. The stepper sits in this card's header, as the
   * monthly summary's does where it has one: it floated on the page between
   * two cards under a "Payroll month" label of its own (critique 2026-10-05
   * #8). Up to the current month, never past it.
   */
  onOffset?: (next: number) => void;
}) {
  const toast = useToast();
  const [rows, setRows] = useState<RegisterRow[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const range = monthRange(orgToday(tz), offset);
  const year = Number(range.from.slice(0, 4));
  const month = Number(range.from.slice(5, 7));
  const label = monthLabel(year, month);
  const status = periods.find((p) => p.year === year && p.month === month)?.status ?? 'open';

  const [leaveTypes, setLeaveTypes] = useState<{ id: string; label: string }[]>([]);
  useEffect(() => {
    if (canRun) void shortfallLeaveTypes().then(setLeaveTypes);
  }, [canRun]);

  const reload = useCallback(async () => {
    const [r, p] = await Promise.all([loadRegister(year, month), listPeriods()]);
    setLoadFailed(!!(r.error || p.error));
    // A locked month is settled: someone with no settlement row had no
    // overtime, so they read 0, not "not yet".
    const locked = p.data.some((x) => x.year === year && x.month === month && x.status !== 'open');
    setRows(
      locked
        ? r.data.map((x) =>
            x.overtime_leave == null ? { ...x, overtime_hours: 0, overtime_leave: 0 } : x,
          )
        : r.data,
    );
    setPeriods(p.data);
  }, [year, month]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const act = async (done: string, run: () => Promise<{ error: { message: string } | null }>) => {
    setBusy(true);
    setErr(null);
    const { error } = await run();
    if (error) setErr(error.message);
    else toast.push({ tone: 'success', message: done });
    await reload();
    setBusy(false);
  };

  const total = rows.reduce((s, r) => s + (r.payable ?? 0), 0);
  // A line with no hours at all is almost never someone who did nothing — it is a
  // month whose time was never logged, or one being priced too early. The formula
  // turns that into a 100% shortfall and a zero payable, quite correctly and
  // quite catastrophically, so say it out loud before anyone exports the file.
  const noHours = rows.filter((r) => Number(r.effective_hours) === 0).length;

  return (
    <PCard
      // The stepper says the month; the heading names it only without one.
      label={onOffset ? 'Payroll register' : `Payroll register · ${label}`}
      right={
        <span
          style={{
            display: 'inline-flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 'var(--hf-space-2)',
          }}
        >
          {onOffset && (
            <PeriodStepper
              label={label}
              labelWidth="7.5rem"
              unit="month"
              onPrev={() => onOffset(offset - 1)}
              onNext={() => onOffset(Math.min(0, offset + 1))}
              nextDisabled={offset >= 0}
            />
          )}
          <PeriodBadge status={status} />
        </span>
      }
    >
      <div
        style={{
          fontSize: 'var(--hf-text-xs)',
          color: 'var(--hf-ink-3)',
          marginBottom: 'var(--hf-space-3)',
        }}
      >
        {PRE_STATUTORY_NOTE}
      </div>
      {monthActions?.(year, month)}
      {err && (
        <div style={{ marginBottom: 'var(--hf-space-2)' }}>
          <InlineStatus tone="error">{err}</InlineStatus>
        </div>
      )}

      {loadFailed ? (
        <LoadError what={`the register for ${label}`} onRetry={() => void reload()} />
      ) : (
        <>
          {noHours > 0 && (
            <div style={{ marginBottom: 'var(--hf-space-3)' }}>
              <InlineStatus tone="warning">
                {noHours === rows.length
                  ? 'Nobody logged any hours this month, so every line is a full shortfall. Check you are pricing the right month before locking it.'
                  : `${noHours} ${noHours === 1 ? 'person has' : 'people have'} no hours recorded this month, so ${noHours === 1 ? 'their line is' : 'those lines are'} a full shortfall.`}
              </InlineStatus>
            </div>
          )}

          {canRun && (
            <div
              style={{
                display: 'flex',
                gap: 'var(--hf-space-2)',
                marginBottom: 'var(--hf-space-3)',
                flexWrap: 'wrap',
              }}
            >
              {status === 'open' && (
                <Button
                  variant="primary"
                  size="sm"
                  loading={busy}
                  onClick={() =>
                    void act(`Payroll computed for ${label}.`, () => runPeriod(year, month))
                  }
                >
                  {rows.length ? 'Recompute' : 'Run payroll'}
                </Button>
              )}
              {status === 'amended' && (
                <Button
                  variant="secondary"
                  size="sm"
                  loading={busy}
                  onClick={() =>
                    void act(`Variance settled for ${label}.`, () => settleAmendment(year, month))
                  }
                >
                  Settle variance
                </Button>
              )}
              {status === 'locked' && (
                <Button
                  variant="secondary"
                  size="sm"
                  loading={busy}
                  onClick={() => void act(`${label} marked paid.`, () => markPaid(year, month))}
                >
                  Mark paid
                </Button>
              )}
              {rows.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  icon="download"
                  onClick={() =>
                    downloadCsv(`hyprflow-payroll-${range.from.slice(0, 7)}.csv`, [
                      COLUMNS.map((c) => c.label),
                      ...rows.map((r) => COLUMNS.map((c) => c.csv(r))),
                    ])
                  }
                >
                  Export
                </Button>
              )}
            </div>
          )}

          {!rows.length ? (
            <div
              style={{
                fontSize: 'var(--hf-text-sm)',
                color: 'var(--hf-ink-3)',
                padding: 'var(--hf-space-2) 0',
              }}
            >
              Nothing computed for {label} yet.
            </div>
          ) : (
            // The card scrolls, never the page. Thirteen columns, and people
            // compare across rows: it keeps them all and scrolls, with the
            // person pinned so a scrolled row still says whose it is
            // (.hf-table-scroll, UI-CONVENTIONS §8).
            <div className="hf-table-scroll">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {COLUMNS.map((c) => (
                      <th
                        key={c.key}
                        scope="col"
                        style={{ ...th, textAlign: c.wide ? 'left' : 'right' }}
                      >
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      {COLUMNS.map((c) => (
                        <td key={c.key} style={{ ...td, textAlign: c.wide ? 'left' : 'right' }}>
                          {c.key === 'settle' &&
                          canRun &&
                          status === 'open' &&
                          Number(r.shortfall_hours) > 0 &&
                          !r.shortfall_waived ? (
                            // Changing it re-prices the month: the run is what
                            // moves the leave balance and the deduction.
                            <div style={{ minWidth: '11rem' }}>
                              <Select
                                ariaLabel={`Shortfall for ${r.full_name ?? 'this person'}`}
                                value={r.shortfall_choice || 'pay'}
                                align="right"
                                options={[
                                  { value: 'pay', label: 'Cut pay' },
                                  ...leaveTypes.map((t) => ({
                                    value: `leave:${t.id}`,
                                    label: `Take ${t.label} leave`,
                                  })),
                                ]}
                                onChange={(v) =>
                                  void act(
                                    `Shortfall for ${r.full_name ?? 'them'} updated.`,
                                    async () => {
                                      const set = await setShortfallChoice(
                                        r.profile_id,
                                        year,
                                        month,
                                        v,
                                      );
                                      return set.error ? set : runPeriod(year, month);
                                    },
                                  )
                                }
                              />
                            </div>
                          ) : c.cell ? (
                            c.cell(r)
                          ) : (
                            (c.csv(r) as React.ReactNode)
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr>
                    {/* One column wide, not spanning to Payable: the first cell
                        is the pinned one, and a pinned cell eleven columns wide
                        slid over the total as the row scrolled. */}
                    <td style={{ ...td, textAlign: 'left', color: 'var(--hf-ink-3)' }}>
                      {rows.length} {rows.length === 1 ? 'person' : 'people'}
                    </td>
                    <td style={td} colSpan={COLUMNS.length - 3} />
                    <td style={{ ...td, fontWeight: 'var(--hf-weight-medium)' }}>
                      <Mono style={{ color: 'var(--hf-ink)' }}>{fmtINRFull(total)}</Mono>
                    </td>
                    <td style={td} />
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </PCard>
  );
}

/** The person's own statements. RLS returns only months that have been closed. */
export function MyPayslip({ uid }: { uid: string }) {
  const [rows, setRows] = useState<PayrollLine[]>([]);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  // null while loading or after a failed read — never an empty breakdown.
  const [parts, setParts] = useState<Component[] | null>(null);
  const [partsFailed, setPartsFailed] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    void myStatements(uid).then(({ data, error }) => {
      if (!live) return;
      setFailed(!!error);
      setRows(data);
    });
    return () => {
      live = false;
    };
  }, [uid, nonce]);

  const loadParts = async (id: string) => {
    setParts(null);
    setPartsFailed(false);
    const { data, error } = await loadComponents(id);
    setPartsFailed(!!error);
    setParts(error ? null : data);
  };
  const toggle = (id: string) => {
    if (open === id) {
      setOpen(null);
      return;
    }
    setOpen(id);
    void loadParts(id);
  };

  if (failed)
    return (
      <PCard label="My statements">
        <LoadError what="your statements" onRetry={() => setNonce((n) => n + 1)} />
      </PCard>
    );
  if (!rows.length) return null;

  return (
    <PCard label="My statements" right={<SensitiveMark label="Only you, HR and admins" />}>
      <div
        style={{
          fontSize: 'var(--hf-text-xs)',
          color: 'var(--hf-ink-3)',
          marginBottom: 'var(--hf-space-3)',
        }}
      >
        {PRE_STATUTORY_NOTE}
      </div>
      {rows.map((r) => {
        const isOpen = open === r.id;
        const panel = `statement-${r.id}`;
        return (
          <div
            key={r.id}
            style={{ borderTop: '1px solid var(--hf-rule)', padding: 'var(--hf-space-2) 0' }}
          >
            {/* A button, not a div with onClick: the breakdown could not be
                opened from the keyboard at all. */}
            <button
              type="button"
              aria-expanded={isOpen}
              aria-controls={panel}
              onClick={() => toggle(r.id)}
              style={{
                width: '100%',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 'var(--hf-space-2) var(--hf-space-3)',
                padding: 'var(--hf-space-1) 0',
                background: 'transparent',
                border: 0,
                cursor: 'pointer',
                textAlign: 'left',
                font: 'inherit',
                color: 'inherit',
              }}
            >
              <span
                style={{
                  fontSize: 'var(--hf-text-sm)',
                  color: 'var(--hf-ink-2)',
                  minWidth: '8rem',
                }}
              >
                {monthLabel(r.period_year, r.period_month)}
              </span>
              <Mono dim>
                {r.effective_hours}h of {r.required_hours}h
              </Mono>
              <span style={{ flex: 1 }} />
              <Mono style={{ fontSize: 'var(--hf-text-md)', color: 'var(--hf-ink)' }}>
                {fmtINRFull(r.payable)}
              </Mono>
              <Icon
                name={isOpen ? 'chevronDown' : 'chevronRight'}
                size={13}
                style={{ color: 'var(--hf-ink-3)' }}
              />
            </button>
            {isOpen && (
              <div id={panel} style={{ marginTop: 'var(--hf-space-2)' }}>
                {partsFailed ? (
                  <LoadError what="this breakdown" onRetry={() => void loadParts(r.id)} />
                ) : parts === null ? (
                  <div style={{ fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' }}>
                    Loading…
                  </div>
                ) : (
                  parts.map((c, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        gap: 'var(--hf-space-3)',
                        fontSize: 'var(--hf-text-sm)',
                        padding: 'var(--hf-space-1) 0',
                        color: 'var(--hf-ink-2)',
                      }}
                    >
                      <span>{c.label}</span>
                      <span style={{ flex: 1 }} />
                      <Mono style={{ color: c.amount < 0 ? 'var(--hf-amber-text)' : undefined }}>
                        {fmtINRFull(c.amount)}
                      </Mono>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}
    </PCard>
  );
}
